<template>
  <!-- Locked: the tab stays in the panel and sells the feature here rather
       than disappearing, so the sidepanel is always present and only its
       content differs. Ordered ahead of the consent branch — an org that
       hasn't bought AI Chat shouldn't be shown its terms. -->
  <div v-if="aiChatGate.isLoading.value" />
  <!-- Entitlement unknown: offer the retry instead of the pitch, so a paying
       org isn't told AI Chat is an add-on it doesn't have. -->
  <div v-else-if="aiChatGate.isError.value" class="overflow-y-auto px-5 py-4">
    <FeatureGateError variant="compact" @retry="aiChatGate.refetch()" />
  </div>
  <div v-else-if="aiChatGate.isLocked.value" class="overflow-y-auto px-5">
    <FeaturePaywall flag="aiChat" variant="compact" />
  </div>
  <!-- Consent pending renders the prompt panel too, and must stay the same
       instance across the unlock: the panel holds the draft it sends once a
       manager accepts. -->
  <template v-else>
    <AIPromptPanel
      v-if="phase === 'prompt' || aiChatGate.isConsentPending.value"
    />
    <!-- Keyed: a chat's turn, stream and polling stay with that chat when
         another is picked. -->
    <AIChatPanel
      v-else-if="sessionId !== null"
      :key="sessionId"
      v-model:initial-message="pendingInitialMessage"
      v-model:initial-attachments="pendingInitialAttachments"
      :session-id="sessionId"
    />
  </template>
</template>

<script setup lang="ts">
import { watch } from "vue";
import AIPromptPanel from "@/components/AIAgent/AIPromptPanel.vue";
import AIChatPanel from "@/components/AIAgent/AIChatPanel.vue";
import { useAiChatSession } from "@/composables/useAiChatSession";
import FeaturePaywall from "@/components/Core/Paywall/FeaturePaywall.vue";
import FeatureGateError from "@/components/Core/Paywall/FeatureGateError.vue";
import { useFeatureGate } from "@/services/feature_gate";

const {
  phase,
  sessionId,
  restart,
  pendingInitialMessage,
  pendingInitialAttachments,
} = useAiChatSession();

const aiChatGate = useFeatureGate("aiChat");

// A chat session can't be continued while consent is pending — one restored
// from before consent lapsed (re-consent after a terms update), or one entered
// some other way meanwhile — and landing back in it once a manager accepts
// would unmount the panel holding the new draft.
watch(
  [() => aiChatGate.isConsentPending.value, phase],
  ([pending, currentPhase]) => {
    if (pending && currentPhase === "chat") restart();
  },
  { immediate: true },
);
</script>
